import { defineComponent as _defineComponent } from 'vue'
import store from "@/store";
  import { ref, onMounted, watch } from "vue";
  import { useToast } from "vue-toastification";
  import ConfirmDeleteModal from "@/components/ui/modals/ConfirmDeleteModal.vue";
  import { AssetGalleryItemInterface } from "@/types/AssetGalleryItemInterface";
  import AssetItemCard from "./AssetItemCard.vue";
  import AddAndUpdateAssetInGalleryModal from "@/components/ui/modals/AddAndUpdateAssetInGalleryModal.vue";
  import {
    CreateExternalColorSchemeInterface,
    CreateInternalColorSchemeInterface,
    ExternalColorSchemeInterface,
    InternalColorSchemeInterface,
    UpdateExternalColorSchemeInterface,
    UpdateInternalColorSchemeInterface,
  } from "@/types/ColorSchemeInterface";
  import {
    CreateFacadeInterface,
    FacadeInterface,
    UpdateFacadeInterface,
  } from "@/types/FacadeInterface";
  import { ColorSchemeType } from "@/enums/ColorSchemeType";
  import { AssetGalleryFormType } from "@/enums/AssetGalleryFormType";
  import InclusionCard from "./InclusionCard.vue";
  import AddAndUpdateInclusionModal from "@/components/ui/modals/AddAndUpdateInclusionModal.vue";
  import {
    CreateInclusionInterface,
    InclusionInterface,
    UpdateInclusionInterface,
  } from "@/types/InclusionInterface";
  import {
    CreateOptionalUpgradeInterface,
    OptionalUpgrade,
    UpdateOptionalUpgradeInterface,
  } from "@/types/OptionalUpgradeInterface";

  type Props = {
    builderId: string;
    title: string;
    galleryType: string;
    fetchAssets: string;
    uploadImage: string;
    reload: boolean;
    deleteAsset: string;
  };
  export type AssetType =
    | FacadeInterface
    | ExternalColorSchemeInterface
    | InternalColorSchemeInterface
    | OptionalUpgrade;

  export type assetInterface = AssetGalleryItemInterface | AssetType;
  type createDtoType =
    | CreateFacadeInterface
    | CreateExternalColorSchemeInterface
    | CreateInternalColorSchemeInterface
    | CreateInclusionInterface
    | CreateOptionalUpgradeInterface;
  type updateDtoType =
    | UpdateFacadeInterface
    | UpdateExternalColorSchemeInterface
    | UpdateInternalColorSchemeInterface
    | UpdateInclusionInterface
    | UpdateOptionalUpgradeInterface;

  type updatePayload = {
    id: string;
    dto: updateDtoType;
  };

  
export default /*@__PURE__*/_defineComponent({
  __name: 'AssetGallery',
  props: {
    builderId: {},
    title: {},
    galleryType: {},
    fetchAssets: {},
    uploadImage: {},
    reload: { type: Boolean },
    deleteAsset: {}
  },
  emits: [
    "openModal",
    "setReload",
    "createAsset",
    "updateAsset",
  ],
  setup(__props: any, { expose: __expose, emit: __emit }) {
  __expose();

  const emits = __emit;
  const props = __props;
  const toast = useToast();

  const assets = ref<AssetGalleryItemInterface[]>([]);
  const isLoading = ref(true);
  const isConfirmDeleteModalOpen = ref(false);
  const assetGalleryModalOpen = ref(false);
  const inclusionModalOpen = ref(false);
  const reloadAssetGallery = ref(false);
  const assetToUpdate = ref<AssetGalleryItemInterface | null>(null);
  const assetToDelete = ref<AssetGalleryItemInterface | null>(null);

  const reloadGallery = async () => {
    await getAssets();
    reloadAssetGallery.value = false;
  };

  const openModal = () => {
    if (props.galleryType === "inclusion") {
      inclusionModalOpen.value = true;
    } else {
      assetGalleryModalOpen.value = true;
    }

    emits("openModal");
  };

  const getAssets = async () => {
    try {
      isLoading.value = true;
      const params: { builderId: string; type?: ColorSchemeType } = {
        builderId: props.builderId.toString(),
      };

      if (props.galleryType === AssetGalleryFormType.EXTERNAL_COLOR_SCHEME) {
        params.type = ColorSchemeType.EXTERNAL;
      } else if (
        props.galleryType === AssetGalleryFormType.INTERNAL_COLOR_SCHEME
      ) {
        params.type = ColorSchemeType.INTERNAL;
      }

      if (props.galleryType === AssetGalleryFormType.OPTIONAL_UPGRADE) {
        const data = await store.dispatch(
          "optionalUpgrade/getOptionalUpgradesByBuilderId",
          {
            builderId: props.builderId,
          }
        );

        assets.value = data;
      } else {
        const data = await store.dispatch(props.fetchAssets, params);

        assets.value = data;
      }
    } catch (error) {
      toast.error(`An error occurred while getting the ${props.title}`);
    } finally {
      isLoading.value = false;
    }
  };

  const createAsset = async (dto: createDtoType, action: string) => {
    try {
      isLoading.value = true;
      const result = await store.dispatch(action, dto);

      emits("createAsset");
      reloadGallery();
      close();
      return result;
    } catch (error) {
      toast.error(`An error occurred while creating the ${props.title}`);
      return null;
    } finally {
      isLoading.value = false;
    }
  };

  const updateAsset = async (payload: updatePayload, action: string) => {
    try {
      isLoading.value = true;
      const result = await store.dispatch(action, payload);

      emits("updateAsset");
      reloadGallery();
      close();
      return result;
    } catch (error) {
      toast.error(`An error occurred while updating the ${props.title}`);
      return null;
    } finally {
      isLoading.value = false;
    }
  };

  const createFacade = async (dto: CreateFacadeInterface) => {
    const result = await createAsset(dto, "facade/createFacade");

    return result;
  };

  const updateFacade = async (
    asset: assetInterface,
    dto: UpdateFacadeInterface
  ) => {
    const payload = { dto, id: asset?._id };
    const result = await updateAsset(payload, "facade/updateFacade");

    return result;
  };

  const createExternalColorScheme = async (
    dto: CreateExternalColorSchemeInterface
  ) => {
    try {
      const result = await store.dispatch("colorScheme/createColorScheme", dto);

      await createAsset(dto, "colorScheme/createColorScheme");
      return result;
    } catch (error) {
      await createAsset(dto, "colorScheme/createColorScheme");
      return null;
    }
  };

  const updateExternalColorScheme = async (
    asset: assetInterface,
    dto: UpdateExternalColorSchemeInterface
  ) => {
    const payload = { dto, id: asset?._id };

    try {
      const result = await store.dispatch(
        "colorScheme/updateColorScheme",
        payload
      );

      reloadGallery();
      return result;
    } catch (error) {
      toast.error(`An error occurred while updating the ${props.title}`);
      return null;
    }
  };

  const createInternalColorScheme = async (
    dto: CreateExternalColorSchemeInterface
  ) => {
    await createAsset(dto, "colorScheme/createColorScheme");
  };
  const updateInternalColorScheme = async (
    asset: assetInterface,
    dto: UpdateInternalColorSchemeInterface
  ) => {
    await updateAsset({ dto, id: asset?._id }, "colorScheme/updateColorScheme");
  };

  const createInclusion = async (dto: CreateInclusionInterface) => {
    await createAsset(dto, "inclusion/createInclusion");
  };
  const updateInclusion = async (
    asset: InclusionInterface,
    dto: UpdateInclusionInterface
  ) => {
    await updateAsset({ dto, id: asset?._id }, "inclusion/updateInclusion");
  };

  const createOptionalUpgrade = async (dto: CreateOptionalUpgradeInterface) => {
    await createAsset(dto, "optionalUpgrade/createOptionalUpgrade");
  };

  const updateOptionalUpgrade = async (
    asset: OptionalUpgrade,
    dto: UpdateOptionalUpgradeInterface
  ) => {
    await updateAsset(
      { dto, id: asset?._id },
      "optionalUpgrade/updateOptionalUpgrade"
    );
  };

  const confirmDelete = (asset: AssetGalleryItemInterface) => {
    assetToDelete.value = asset;
    isConfirmDeleteModalOpen.value = true;
  };

  const deleteAsset = async (asset: AssetGalleryItemInterface) => {
    try {
      isLoading.value = true;
      await store.dispatch(props.deleteAsset, {
        id: asset._id,
      });
      assetToDelete.value = null;
      await getAssets();
    } catch (error) {
      toast.error(
        `An error occurred while deleting the ${props.title} ${asset?.name}`
      );
    } finally {
      isLoading.value = false;
    }
  };

  onMounted(() => getAssets());

  watch(
    () => props.builderId,
    () => getAssets()
  );

  watch(
    () => props.reload,
    () => {
      if (!props.reload) return;

      getAssets();
      emits("setReload");
    }
  );

const __returned__ = { emits, props, toast, assets, isLoading, isConfirmDeleteModalOpen, assetGalleryModalOpen, inclusionModalOpen, reloadAssetGallery, assetToUpdate, assetToDelete, reloadGallery, openModal, getAssets, createAsset, updateAsset, createFacade, updateFacade, createExternalColorScheme, updateExternalColorScheme, createInternalColorScheme, updateInternalColorScheme, createInclusion, updateInclusion, createOptionalUpgrade, updateOptionalUpgrade, confirmDelete, deleteAsset, get store() { return store }, ref, onMounted, watch, get useToast() { return useToast }, ConfirmDeleteModal, get AssetGalleryItemInterface() { return AssetGalleryItemInterface }, AssetItemCard, AddAndUpdateAssetInGalleryModal, get CreateExternalColorSchemeInterface() { return CreateExternalColorSchemeInterface }, get CreateInternalColorSchemeInterface() { return CreateInternalColorSchemeInterface }, get ExternalColorSchemeInterface() { return ExternalColorSchemeInterface }, get InternalColorSchemeInterface() { return InternalColorSchemeInterface }, get UpdateExternalColorSchemeInterface() { return UpdateExternalColorSchemeInterface }, get UpdateInternalColorSchemeInterface() { return UpdateInternalColorSchemeInterface }, get CreateFacadeInterface() { return CreateFacadeInterface }, get FacadeInterface() { return FacadeInterface }, get UpdateFacadeInterface() { return UpdateFacadeInterface }, get ColorSchemeType() { return ColorSchemeType }, get AssetGalleryFormType() { return AssetGalleryFormType }, InclusionCard, AddAndUpdateInclusionModal, get CreateInclusionInterface() { return CreateInclusionInterface }, get InclusionInterface() { return InclusionInterface }, get UpdateInclusionInterface() { return UpdateInclusionInterface }, get CreateOptionalUpgradeInterface() { return CreateOptionalUpgradeInterface }, get OptionalUpgrade() { return OptionalUpgrade }, get UpdateOptionalUpgradeInterface() { return UpdateOptionalUpgradeInterface } }
Object.defineProperty(__returned__, '__isScriptSetup', { enumerable: false, value: true })
return __returned__
}

})